/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  Inputs,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { useNavigate } from 'react-router-dom';

interface FormValues {
  newEmail: string;
}

interface EditAgentEmailFormProps {
  open: boolean;
  onClose: () => void;
  agent: Agent;
  onSubmitCallback?: (data: FormValues | undefined) => void;
}

export const EditAgentEmailForm = ({
  open,
  onClose,
  agent,
  onSubmitCallback,
}: EditAgentEmailFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [submissionError, setSubmissionError] = React.useState<ApiError>();
  const [userExistsPassed, setUserExistsPassed] = React.useState(false);
  const { mutate: mutateAgent } = useFetch<Agent>(apiClient.peopleClient.getAgent, [agent.id], !!agent.id);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      newEmail: '',
    },
  });

  const handleClose = () => {
    reset();
    setSubmissionError(undefined);
    setUserExistsPassed(false);
    onClose();
  };

  const validateEmailFormat = (email: string): string | boolean => {
    if (!email) return 'Please enter a new email address';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return true;
  };

  const debouncedUserExists = React.useCallback(
    debounce(async (email: string) => {
      if (validateEmailFormat(email) !== true) {
        return;
      }

      try {
        const exists = await apiClient.authClient.userExists(email);
        if (exists) {
          setError('newEmail', {
            type: 'manual',
            message: 'This email is already registered.',
          });
          setUserExistsPassed(false);
        } else {
          setUserExistsPassed(true);
        }
      } catch (error) {
        setError('newEmail', {
          type: 'manual',
          message: 'Unable to validate email. Please try again.',
        });
        setUserExistsPassed(false);
      }
    }, 500),
    [apiClient.authClient, setError, setUserExistsPassed],
  );

  const handleEmailChange = (email: string) => {
    setUserExistsPassed(false);
    if (email) {
      debouncedUserExists(email);
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const user = agent.userInfo[0];
      await apiClient.authClient.putUsername(user.id, { id: user.id, email: data.newEmail });
      if (onSubmitCallback) onSubmitCallback(data);
      enqueueSnackbar('Agent email updated.', { variant: 'success' });
      mutateAgent();
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.code === 401) navigate('/logout');
      setSubmissionError(apiError);
      enqueueSnackbar('Failed to update agent email', { variant: 'default' });
    }
  };

  return (
    <FormDialog open={open}>
      <FormDialogTitle handleClose={handleClose}>Edit Agent Email</FormDialogTitle>
      <FormElement name="agent-email-editor-form" onSubmit={handleSubmit(onSubmit)}>
        <FormDialogContent>
          <FormInputsStack>
            {submissionError && (
              <FormSubmissionError
                message={submissionError.message}
                errorCode={submissionError.code}
                errorId={submissionError.id}
              />
            )}
            <Inputs.Text
              id="current-email"
              value={agent.email}
              label="Current Email Address"
              readOnly
            />
            <Controller
              control={control}
              name="newEmail"
              rules={{
                required: 'Please enter a new email address',
                validate: validateEmailFormat,
              }}
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="new-email"
                  error={!!errors?.newEmail}
                  helperText={errors?.newEmail?.message}
                  label="New Email Address"
                  required
                  onChange={(event) => {
                    field.onChange(event);
                    handleEmailChange(event.target.value);
                  }}
                />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Note: Agent will get a confirmation email and will need to set a new password.
            </Typography>
          </FormInputsStack>
        </FormDialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={handleClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={!isValid || !userExistsPassed}
          >
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </FormDialog>
  );
};

export default EditAgentEmailForm;
